import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../../utils/api';

// Cache for glass data to avoid repeated API calls
// Keyed by appliesTo value to support different product types
const glassDataCacheByType = {};
const cachePromiseByType = {};

/**
 * Hook to fetch and manage glass data from the backend
 * @param {string} appliesTo - Filter by product type: 'windows', 'patiodoors', or undefined for all
 * Data structure from API:
 * {
 *   glazingTypes: [{ id, label, value, active, sort_order }],
 *   texturedDesigns: [{ id, label, value, active, sort_order }],
 *   tintedDesigns: [{ id, label, value, active, sort_order }],
 *   glassEnergy: [{ id, label, value, active, sort_order }],
 *   glassStrength: [{ id, label, value, active, sort_order }],
 *   glassThickness: [{ id, label, value, active, sort_order }]
 * }
 */
export const useGlassData = (appliesTo = 'windows') => {
  const cacheKey = appliesTo || 'all';
  const [data, setData] = useState(glassDataCacheByType[cacheKey]);
  const [loading, setLoading] = useState(!glassDataCacheByType[cacheKey]);
  const [error, setError] = useState(null);

  const fetchGlassData = useCallback(async (forceRefresh = false) => {
    // Return cached data if available and not forcing refresh
    if (glassDataCacheByType[cacheKey] && !forceRefresh) {
      setData(glassDataCacheByType[cacheKey]);
      setLoading(false);
      return glassDataCacheByType[cacheKey];
    }

    // If already fetching, wait for that promise
    if (cachePromiseByType[cacheKey] && !forceRefresh) {
      try {
        const result = await cachePromiseByType[cacheKey];
        setData(result);
        setLoading(false);
        return result;
      } catch (e) {
        setError(e.message);
        setLoading(false);
        return null;
      }
    }

    setLoading(true);
    setError(null);

    // Build URL with optional applies_to filter
    const url = appliesTo ? `/glass/?applies_to=${appliesTo}` : '/glass/';

    cachePromiseByType[cacheKey] = authFetch(url)
      .then((result) => {
        glassDataCacheByType[cacheKey] = result;
        setData(result);
        setLoading(false);
        return result;
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
        cachePromiseByType[cacheKey] = null;
        throw e;
      });

    return cachePromiseByType[cacheKey];
  }, [appliesTo, cacheKey]);

  useEffect(() => {
    if (!glassDataCacheByType[cacheKey]) {
      fetchGlassData();
    }
  }, [fetchGlassData, cacheKey]);

  // Get active glazing types
  const getGlazingTypes = useCallback(() => {
    if (!data?.glazingTypes) return [];
    return data.glazingTypes.filter(t => t.active);
  }, [data]);

  // Get active textured designs
  const getTexturedDesigns = useCallback(() => {
    if (!data?.texturedDesigns) return [];
    return data.texturedDesigns.filter(t => t.active);
  }, [data]);

  // Get active tinted designs
  const getTintedDesigns = useCallback(() => {
    if (!data?.tintedDesigns) return [];
    return data.tintedDesigns.filter(t => t.active);
  }, [data]);

  // Get active glass energy options
  const getGlassEnergy = useCallback(() => {
    if (!data?.glassEnergy) return [];
    return data.glassEnergy.filter(e => e.active);
  }, [data]);

  // Get active glass strength options
  const getGlassStrength = useCallback(() => {
    if (!data?.glassStrength) return [];
    return data.glassStrength.filter(s => s.active);
  }, [data]);

  // Get active glass thickness options
  const getGlassThickness = useCallback(() => {
    if (!data?.glassThickness) return [];
    return data.glassThickness.filter(t => t.active);
  }, [data]);

  // Clear cache (useful for admin updates)
  const clearCache = useCallback(() => {
    Object.keys(glassDataCacheByType).forEach(key => {
      delete glassDataCacheByType[key];
    });
    Object.keys(cachePromiseByType).forEach(key => {
      delete cachePromiseByType[key];
    });
  }, []);

  return {
    data,
    loading,
    error,
    refresh: () => fetchGlassData(true),
    clearCache,
    getGlazingTypes,
    getTexturedDesigns,
    getTintedDesigns,
    getGlassEnergy,
    getGlassStrength,
    getGlassThickness
  };
};

export default useGlassData;
